import React, { useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/services/api";
import { ArrowLeft, Download, Copy, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import InvoiceDocument from "@/components/InvoiceDocument";
import { buildFromApiResponse } from "@/components/invoice";
import { downloadInvoicePDF } from "@/utils/pdf";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneOrgId, setCloneOrgId] = useState("");
  const [copyType, setCopyType] = useState("original");
  const [showCopyDropdown, setShowCopyDropdown] = useState(false);

  const { data: invoice, isLoading: isInvoiceLoading } = useQuery({
    queryKey: ["invoice", id],
    queryFn: async () => (await api.get(`/invoice/${id}`)).data,
  });

  const { data: invoiceStatus } = useQuery({
    queryKey: ["invoice-status", id],
    queryFn: async () => (await api.get(`/invoices/${id}/status`)).data,
    enabled: !!id,
  });

  const { data: orgs = [] } = useQuery({
    queryKey: ["orgs"],
    queryFn: async () => (await api.get("/organizations")).data,
  });
  
  // Use versioned data if available, otherwise fall back to fetching current data
  const { data: org, isLoading: isOrgLoading } = useQuery({
    queryKey: ["organization", invoice?.organization_id],
    queryFn: async () => {
      // Use versioned organization data if available
      if (invoice?.organization_version) {
        return invoice.organization_version;
      }
      // Fall back to current organization data
      const list = (await api.get("/organizations")).data;
      return list.find((o) => o.id === invoice.organization_id);
    },
    enabled: !!invoice,
  });
  const { data: template, isLoading: isTemplateLoading } = useQuery({
    queryKey: ["template", invoice?.template_id, invoice?.template_version_id],
    queryFn: async () => {
      // Use versioned template data if available
      if (invoice?.template_version) {
        return invoice.template_version;
      }
      // Fall back to current template data
      if (invoice?.template_id) {
        return (await api.get(`/templates/${invoice.template_id}`)).data;
      }
      return (await api.get(`/template/${invoice.organization_id}`)).data;
    },
    enabled: !!invoice,
  });
  const { data: customer, isLoading: isCustomerLoading } = useQuery({
    queryKey: ["customer-of", invoice?.customer_id],
    queryFn: async () => {
      // Use versioned customer data if available
      if (invoice?.customer_version) {
        return invoice.customer_version;
      }
      // Fall back to current customer data
      const list = (await api.get("/customers", { params: { organization_id: invoice.organization_id } })).data;
      return list.find((c) => c.id === invoice.customer_id);
    },
    enabled: !!invoice,
  });
  
  // Get shipped customer using versioned data if available
  const customerShipped = invoice?.customer_shipped_version || (invoice?.customer_id_shipped ? customer : null);

  // Construct the canonical display model using the API response
  const model = useMemo(() => {
    if (!invoice || !org || !customer || !template) return null;
    return buildFromApiResponse({
      invoice,
      organization: org,
      customer,
      customerShipped,
      template,
      copyType,
    });
  }, [invoice, org, customer, customerShipped, template, copyType]);

  const download = async () => {
    try {
      if (!model) return;
      const copyLabel = copyType === "original" ? "Original" : copyType === "duplicate" ? "Duplicate" : "Triplicate";
      await downloadInvoicePDF(
        model,
        `${invoice.invoice_number}_${copyLabel}.pdf`
      );
    } catch (e) {
      toast.error("PDF failed: " + e.message);
    }
  };

  const handleClone = () => {
    if (!invoice) return;
    
    // Convert absolute tax amounts to percentages for cloning
    const clonedSubtotal = invoice.subtotal || 0;
    const clonedDiscount = invoice.discount || 0;
    const taxableAmount = clonedSubtotal - clonedDiscount;
    
    const cgstPercent = taxableAmount > 0 ? Math.round(((invoice.cgst / taxableAmount) * 100) * 100) / 100 : 0;
    const sgstPercent = taxableAmount > 0 ? Math.round(((invoice.sgst / taxableAmount) * 100) * 100) / 100 : 0;
    const igstPercent = taxableAmount > 0 ? Math.round(((invoice.igst / taxableAmount) * 100) * 100) / 100 : 0;
    
    // Convert discount from absolute to percentage
    const discountPercent = clonedSubtotal > 0 ? Math.round(((clonedDiscount / clonedSubtotal) * 100) * 100) / 100 : 0;
    
    const targetOrgId = cloneOrgId || invoice.organization_id;
    
    nav(`/app/invoices/new`, {
      state: {
        cloneData: {
          ...invoice,
          organization_id: targetOrgId,
          discount: discountPercent,
          cgst: cgstPercent,
          sgst: sgstPercent,
          igst: igstPercent,
          roundoff: invoice.roundoff || 0,
        },
      },
    });
    setShowCloneModal(false);
    toast.success("Opening cloned invoice...");
  };

  const handleRecordPayment = () => {
    if (!invoice) return;
    // Navigate to a payment recording page with invoice pre-filled
    nav(`/app/payments/new`, { state: { invoiceId: invoice.id, customerId: invoice.customer_id } });
  };

  // Show loading skeleton until all required data is loaded
  const isLoadingData = isInvoiceLoading || !invoice || isOrgLoading || isTemplateLoading || isCustomerLoading;
  
  if (isLoadingData) {
    return (
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
          <div className="h-10 w-20 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="h-10 w-24 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
            <div className="h-10 w-32 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
            <div className="h-10 w-40 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
          </div>
        </div>
        <div className="bg-slate-100 dark:bg-slate-900 p-6 rounded-sm">
          <div className="space-y-4">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
            <div className="h-64 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
            <div className="h-32 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-sm"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white self-start" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleRecordPayment}
            className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:border-slate-900 dark:hover:border-white"
            data-testid="record-payment-detail-button"
          >
            <span className="font-bold">₹</span> Record Payment
          </button>
          <button
            onClick={() => { setShowCloneModal(true); setCloneOrgId(invoice.organization_id); }}
            className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:border-slate-900 dark:hover:border-white"
            data-testid="clone-detail-button"
          >
            <Copy className="w-4 h-4" /> Clone
          </button>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowCopyDropdown(!showCopyDropdown)}
                className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:border-slate-900 dark:hover:border-white"
              >
                {copyType === "original" ? "Original" : copyType === "duplicate" ? "Duplicate" : "Triplicate"} <ChevronDown className="w-4 h-4" />
              </button>
              {showCopyDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm shadow-lg z-10">
                  <button
                    onClick={() => { setCopyType("original"); setShowCopyDropdown(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    Original Copy
                  </button>
                  <button
                    onClick={() => { setCopyType("duplicate"); setShowCopyDropdown(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    Duplicate Copy
                  </button>
                  <button
                    onClick={() => { setCopyType("triplicate"); setShowCopyDropdown(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    Triplicate Copy
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="download-detail-pdf"
              onClick={download}
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 rounded-sm hover:bg-[#001F73] font-semibold"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* Payment Status Section */}
      {invoice && invoiceStatus && (
        <div className="mb-6 p-6 bg-white dark:bg-[#121214] border border-slate-200 dark:border-slate-800 rounded-sm">
          <h3 className="text-lg font-semibold mb-4">Payment Status</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-slate-500 mb-1">Total Billed</div>
              <div className="text-xl font-bold">₹{Number(invoiceStatus.total_debit || 0).toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500 mb-1">Total Paid</div>
              <div className="text-xl font-bold text-emerald-600">₹{Number(invoiceStatus.total_credit || 0).toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500 mb-1">Balance Due</div>
              <div className={`text-xl font-bold ${invoiceStatus.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                ₹{Number(invoiceStatus.current_balance || 0).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-slate-500">Status: </span>
                <span className={`font-semibold ${invoiceStatus.calculated_status === 'paid' ? 'text-emerald-600' : invoiceStatus.calculated_status === 'overdue' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {invoiceStatus.calculated_status?.toUpperCase()}
                </span>
              </div>
              {invoiceStatus.due_date && (
                <div className="text-sm text-slate-500">
                  Due: {invoiceStatus.due_date}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="overflow-auto bg-slate-100 dark:bg-slate-900 p-6 rounded-sm">
        <InvoiceDocument
          model={model}
          showToolbar={false}
        />
      </div>

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#121214] rounded-sm p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Clone Invoice</h3>
            <p className="text-sm text-slate-500 mb-4">Select organization to clone this invoice to. The invoice number will be auto-generated based on the selected organization.</p>
            <label className="block mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Target Organization</span>
              <select
                value={cloneOrgId}
                onChange={(e) => setCloneOrgId(e.target.value)}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
              >
                {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
              </select>
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowCloneModal(false); setCloneOrgId(""); }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleClone}
                className="px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73]"
                data-testid="confirm-clone-detail-button"
              >
                Clone Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
